import {
  GenrePayload,
  ReleaseVersion,
  UpdateReleaseVersionSongPatchData,
} from 'types/releases';
import {
  GetGenres,
  LabelVersionDetailsFormData,
} from 'app/components/Releases/LabelVersionDetailsForm/types';
import { DDL, Option } from 'types/general.types';
import { emptyOptionToNull, emptyStringToNull } from 'app/utils/utils';
import pickBy from 'lodash/pickBy';
import React from 'react';

const getGenres: GetGenres = (genresDDL, genresRequest, releaseVersion) => {
  if (!genresRequest?.length) {
    return undefined;
  }

  const snapshotGenres = releaseVersion.song.snapshot.genres || [];
  const overridesUndefined = !releaseVersion.song.overrides.genres;

  const transferSnapshotGenres = (): GenrePayload[] =>
    snapshotGenres.map((genre) => ({
      genre: genresDDL[genre].key,
      op: 'ADD',
    }));

  const omitRemoveOperations = (
    addOperations: LabelVersionDetailsFormData['genres'],
    anyOperations: LabelVersionDetailsFormData['genres']
  ) => {
    if (!addOperations?.length) {
      return [];
    }
    return addOperations.filter(
      (addOperation) =>
        !anyOperations?.find(
          (operation) => addOperation.genre === operation.genre
        )
    );
  };

  const getAddOperations = (
    addOperations: LabelVersionDetailsFormData['genres']
  ) => addOperations?.filter((operation) => operation.op === 'ADD') || [];

  if (overridesUndefined) {
    const transfer = transferSnapshotGenres();
    const filtered = omitRemoveOperations(transfer, genresRequest);
    const addOperations = getAddOperations(genresRequest);
    const overrides = [...filtered, ...addOperations];
    return overrides.length ? overrides : undefined;
  }

  return genresRequest;
};

export const convertSongVersionFormToApiRequestData: (
  data: Partial<LabelVersionDetailsFormData>,
  releaseVersion: ReleaseVersion,
  genresDDL: DDL
) => UpdateReleaseVersionSongPatchData = (data, releaseVersion, genresDDL) => {
  const { snapshot } = releaseVersion.song;

  const handleTextField = ({
    snapshot,
    override,
  }: {
    snapshot?: string | number;
    override?: string | number;
  }) => {
    if (snapshot === override && override !== undefined) {
      return null;
    }
    return emptyStringToNull(override as string);
  };

  const handleOptionField = ({
    snapshot,
    override,
  }: {
    snapshot?: string;
    override?: Option | null;
  }) => {
    if (snapshot === override?.value && override !== undefined) {
      return null;
    }
    return emptyOptionToNull(override);
  };

  const payload = {
    versionTitle: data.versionTitle,
    altTitle: handleTextField({
      snapshot: snapshot.altTitle,
      override: data.altTitle,
    }),
    displayArtistName: handleTextField({
      snapshot: snapshot.displayArtistName,
      override: data.displayArtistName,
    }),
    tempo: handleTextField({
      snapshot: snapshot.tempo,
      override: data.tempo,
    }),
    language: handleOptionField({
      snapshot: snapshot.language,
      override: data.language,
    }),
    keySignature: handleOptionField({
      snapshot: snapshot.keySignature,
      override: data.keySignature,
    }),
    timeSignature: handleOptionField({
      snapshot: snapshot.timeSignature,
      override: data.timeSignature,
    }),
    location:
      data.location || data.location === ''
        ? {
            address: handleTextField({
              snapshot: snapshot.location?.address || '',
              override: data.location,
            }),
          }
        : undefined,
    explicit: data.explicit,
    genres: getGenres(genresDDL, data.genres, releaseVersion),
    duration: (handleTextField({
      snapshot: snapshot.duration,
      override: data.duration,
    }) as unknown) as number,
  };
  return pickBy(payload, (value) => value !== undefined); // Cleans object of undefined values/keys
};

export const getFieldFallback = (
  name: string,
  releaseVersion: ReleaseVersion
) => {
  if (name === 'location') {
    return releaseVersion.song.snapshot.location?.address || '';
  }

  if (name === 'versionTitle') {
    return releaseVersion.metadata.versionTitle || '';
  }

  return releaseVersion.song.snapshot[name] || '';
};

export const getAutocompleteFallback = (
  name: string,
  options: Option[],
  releaseVersion: ReleaseVersion,
  event: React.FocusEvent<HTMLInputElement>
) => {
  const value = event.target?.value;
  const fallback = options.find(
    (option) => option.value === releaseVersion.song.snapshot[name]
  );
  return { value, fallback };
};
